import type { ReactNode } from 'react';
import { useContext, useMemo, useReducer, createContext } from 'react';
import unionBy from 'lodash/unionBy';

//types
import type {
    InitialContextType,
    SearchBoxItem
} from '@/utils/hooks/context/searchBox/contextConfig';
import type { SearchBoxReducerActionsType } from '@/utils/hooks/context/searchBox/Actions';

//actions
import {
    ADD_ITEM,
    REMOVE_ITEM,
    SELECT_ONLY_ONE,
    SELECT_ALL,
    UNSELECT_ALL,
    SET_FILTERED_DATA,
    SET_IS_DISABLED,
    SET_INPUT_VALUE,
    SELECT_MULTIPLE,
    UNSELECT_MULTIPLE,
    UNSELECT_EXPECT_FIRST_ONE
} from '@/utils/hooks/context/searchBox/Actions';

// Context configuration

const SearchBoxReducer = <TItem extends SearchBoxItem>(
    state: InitialContextType<TItem>,
    action: SearchBoxReducerActionsType<TItem>
) => {
    switch (action.type) {
        case ADD_ITEM: {
            return {
                ...state,
                filteredData: state.filteredData.map((item) => {
                    if (item.id === action.payload.id) {
                        return {
                            ...item,
                            selected: true
                        };
                    }
                    return item;
                }),
                initialData: state.initialData.map((item) => {
                    if (item.id === action.payload.id) {
                        return {
                            ...item,
                            selected: true
                        };
                    }
                    return item;
                }),
                selectedItems: [...state.selectedItems, { ...action.payload, selected: true }]
            };
        }
        case REMOVE_ITEM: {
            return {
                ...state,
                filteredData: state.filteredData.map((item) => {
                    if (item.id === action.payload.id) {
                        return {
                            ...item,
                            selected: false
                        };
                    }
                    return item;
                }),
                initialData: state.initialData.map((item) => {
                    if (item.id === action.payload.id) {
                        return {
                            ...item,
                            selected: false
                        };
                    }
                    return item;
                }),
                selectedItems: state.selectedItems.filter((item) => item.id !== action.payload.id)
            };
        }
        case SELECT_ONLY_ONE: {
            // Add selected property to the selected item
            const selectedItems = action.payload;
            selectedItems['selected'] = true;
            return {
                ...state,
                filteredData: state.filteredData.map((item) => {
                    if (item.id !== action.payload.id && item.selected) {
                        return {
                            ...item,
                            selected: false
                        };
                    }
                    if (item.id === action.payload.id && !item.selected) {
                        return {
                            ...item,
                            selected: true
                        };
                    }
                    return item;
                }),
                initialData: state.initialData.map((item) => {
                    if (item.id !== action.payload.id && item.selected) {
                        return {
                            ...item,
                            selected: false
                        };
                    }
                    if (item.id === action.payload.id && !item.selected) {
                        return {
                            ...item,
                            selected: true
                        };
                    }
                    return item;
                }),
                selectedItems: [selectedItems]
            };
        }
        case SELECT_MULTIPLE: {
            const multipleSelected = state.filteredData.map((item) => {
                if (action.payload.includes(item.id) && !item?.disabled) {
                    return { ...item, selected: true };
                }

                return item;
            });

            return {
                ...state,
                filteredData: multipleSelected,
                initialData: state.initialData.map((item) => {
                    if (action.payload.includes(item.id) && !item?.disabled) {
                        return { ...item, selected: true };
                    }
                    return item;
                }),
                selectedItems: unionBy(
                    state.selectedItems,
                    multipleSelected.filter((item) => item.selected),
                    'id'
                )
            };
        }
        case UNSELECT_MULTIPLE: {
            return {
                ...state,
                filteredData: state.filteredData.map((item) => {
                    if (action.payload.includes(item.id)) {
                        return { ...item, selected: false };
                    }
                    return item;
                }),
                initialData: state.initialData.map((element) => {
                    if (action.payload.includes(element.id)) {
                        return { ...element, selected: false };
                    }
                    return element;
                }),
                selectedItems: state.selectedItems.filter(
                    (item) => !action.payload.includes(item.id)
                )
            };
        }
        case SELECT_ALL: {
            const allSelected = state.filteredData.map((item) => {
                if (!item?.disabled) {
                    return { ...item, selected: true };
                }
                return item;
            });
            const selectedIds = allSelected.map((item) => item.id);
            const newData = state.initialData.map((element) => {
                if (selectedIds.includes(element.id) && !element?.disabled) {
                    return { ...element, selected: true };
                }
                return element;
            });

            return {
                ...state,
                filteredData: allSelected,
                initialData: newData,
                selectedItems: unionBy(
                    state.selectedItems,
                    allSelected.filter((item) => !item?.disabled),
                    'id'
                )
            };
        }
        case UNSELECT_ALL: {
            const allUnSelected = state.filteredData.map((item) => {
                return { ...item, selected: false };
            });
            const unSelectedIds = allUnSelected.map((item) => item.id);
            const unselectedItems = state.selectedItems.filter(
                (selectedItem) => !unSelectedIds.includes(selectedItem.id)
            );
            const newData = state.initialData.map((element) => {
                if (unSelectedIds.includes(element.id)) {
                    return { ...element, selected: false };
                }
                return element;
            });

            return {
                ...state,
                filteredData: allUnSelected,
                initialData: newData,
                selectedItems: unselectedItems
            };
        }
        case UNSELECT_EXPECT_FIRST_ONE: {
            const selectedItemsFromFilteredData = state.filteredData.filter(
                (item) => item.selected
            );
            const selectedItemsFromInitialData = state.initialData.filter((item) => item.selected);
            const allUnSelected = state.filteredData.map((item, key) => {
                if (
                    key !== 0 ||
                    selectedItemsFromFilteredData.length !== selectedItemsFromInitialData.length
                ) {
                    return { ...item, selected: false };
                }

                return item;
            });
            const unSelectedIds = allUnSelected
                .filter((item) => !item.selected)
                .map((item) => item.id);
            const unselectedItems = state.selectedItems.filter(
                (selectedItem) => !unSelectedIds.includes(selectedItem.id)
            );
            const newData = state.initialData.map((element) => {
                if (unSelectedIds.includes(element.id)) {
                    return { ...element, selected: false };
                }
                return element;
            });

            return {
                ...state,
                filteredData: allUnSelected,
                initialData: newData,
                selectedItems: unselectedItems
            };
        }
        case SET_FILTERED_DATA: {
            return {
                ...state,
                filteredData: action.payload
            };
        }
        case SET_IS_DISABLED: {
            return {
                ...state,
                disabled: action.payload
            };
        }
        case SET_INPUT_VALUE: {
            return {
                ...state,
                inputValue: action.payload
            };
        }
    }
};

type Dispatch<TItem extends SearchBoxItem = SearchBoxItem> = (
    action: SearchBoxReducerActionsType<TItem>
) => void;

// Pass "static" type here by expecting TItem is of type SearchBoxItem
const SearchBoxContext = createContext<{
    dataContext: InitialContextType;
    dispatch: Dispatch;
}>(undefined);

type SearchBoxProviderProps<TItem extends SearchBoxItem> = {
    value: Partial<InitialContextType<TItem>>;
    children: ReactNode;
};
const SearchBoxProvider = <TItem extends SearchBoxItem>({
    value,
    children
}: SearchBoxProviderProps<TItem>) => {
    const [state, dispatch] = useReducer(SearchBoxReducer<TItem>, {
        initialData: [],
        filteredData: [],
        selectedItems: [],
        searchKeys: {},
        inputId: '',
        inputValue: '',
        globalError: null,
        disabled: false,
        isMultiSelect: true,
        ...value
    });

    const values = useMemo(() => ({ dataContext: state, dispatch }), [state, dispatch]);

    return <SearchBoxContext.Provider value={values}>{children}</SearchBoxContext.Provider>;
};
const useSearchBoxContext = <TItem extends SearchBoxItem>() => {
    const context = useContext(SearchBoxContext);
    if (!context) {
        throw new Error('useSearchBoxContext must be used within a SearchBoxProvider');
    }
    //Grab TItem from the hook call to ensure the correct type is inferred and returned
    return context as { dataContext: InitialContextType<TItem>; dispatch: Dispatch<TItem> };
};

export { SearchBoxProvider, useSearchBoxContext };
